import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  styled,
  IconButton,
  Tooltip,
} from "@mui/material";

import { Fragment, useCallback, useMemo, useState } from "react";
import { KeyboardArrowDown, KeyboardArrowRight, Summarize } from "@mui/icons-material";
import dayjs from "dayjs";
import ApiService from "@/api/ApiService";
import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { DataGridPro, GridColDef, GridRenderCellParams, GridRowClassNameParams, useGridApiContext, useGridApiRef } from "@mui/x-data-grid-pro";
import ManyToOneSelect from "@/shared/forms/ManyToOneSelect";
import { usePersistentDataGrid } from "@/shared/hooks/usePersistentDataGrid";

type AmountMode = "offered" | "ordered" | "open";

type AmountCategory = "Mischgut" | "Schüttgut" | "Deponie/Recycling" | "Sonstiges";

type TreeLevel = "customer" | "project" | "order" | "subGroup" | "material";

type AmountValues = Record<AmountMode, number>;

type AmountListRow = {
  order_number: string;
  valid_from: string;
  valid_until: string;
  project_number: string;
  project_name: string;
  customer_name: string;
  material_number: string;
  material_name: string;
  unit: string;
  product_group: string | null;
  product_sub_group: string | null;
  offered: number | string;
  ordered: number | string;
  open: number | string;
};

type AmountTreeRow = {
  id: string;
  path: string;
  level: TreeLevel;
  label: string;
  number?: string;
  name?: string;
  values: Record<AmountCategory, AmountValues>;
  units: Record<AmountCategory, string>;
};

type InternalAmountTreeRow = AmountTreeRow & {
  unitSets: Record<AmountCategory, Set<string>>;
};

const TREE_SEPARATOR = "###";

const AMOUNT_CATEGORIES: AmountCategory[] = ["Mischgut", "Schüttgut", "Deponie/Recycling", "Sonstiges"];

const MISCHGUT_GROUPS = new Set(["ASPHALT", "Asphaltmischgut"]);

const SCHUETTGUT_GROUPS = new Set([
  "Einfach gebrochenes Material",
  "Ungebrochenes Material",
  "Edelsplitte",
  "Quarzit-Material",
  "Material Kappelbach",
]);

const DEPONIE_RECYCLING_GROUPS = new Set(["Recycling-Material", "Deponie", "Pechhaltigesmaterial (Entsorgungspflichtig"]);

const getTreeDataPath = (row: AmountTreeRow) => row.path.split(TREE_SEPARATOR);

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  "& .amountlist_level1": {
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: "hsl(227, 100%, 99%)",
    },
  },
  "& .amountlist_level2": {
    backgroundColor: "hsl(227, 100%, 98%)",
    "&:hover": {
      backgroundColor: "hsl(227, 100%, 97%)",
    },
  },
  "& .amountlist_level3": {
    backgroundColor: "hsl(227, 100%, 96%)",
    "&:hover": {
      backgroundColor: "hsl(227, 100%, 95%)",
    },
  },
  "& .amountlist_level4": {
    backgroundColor: "hsl(227, 100%, 94%)",
    "&:hover": {
      backgroundColor: "hsl(227, 100%, 93%)",
    },
  },
  "& .amountlist_level5": {
    backgroundColor: "hsl(227, 100%, 92%)",
    "&:hover": {
      backgroundColor: "hsl(227, 100%, 91%)",
    },
  },
}));

const toNumber = (value: number | string | null | undefined) => {
  const parsed = parseFloat(String(value ?? "0").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};

const amountFormatter = new Intl.NumberFormat("de-DE", {
  maximumFractionDigits: 3,
});

const formatAmount = (value: number | string | null | undefined, unit: string) => {
  const amount = toNumber(value);

  if (!amount) {
    return "";
  }

  return `${amountFormatter.format(amount)} ${unit ?? ""}`.trim();
};

const getAmountValues = (row: AmountListRow): AmountValues => ({
  offered: toNumber(row.offered),
  ordered: toNumber(row.ordered),
  open: 0,
});

const normalizeUnit = (unit: string | null | undefined) => {
  if (!unit) return "t";
  if (unit === "to") return "t";
  return unit;
};

const getResolvedUnit = (units: Set<string>) => {
  if (units.size === 1) {
    return Array.from(units)[0];
  }

  return "t";
};

const getAmountCategory = (productGroup: string | null | undefined): AmountCategory => {
  const normalizedProductGroup = productGroup ?? "";

  if (MISCHGUT_GROUPS.has(normalizedProductGroup)) {
    return "Mischgut";
  }

  if (SCHUETTGUT_GROUPS.has(normalizedProductGroup)) {
    return "Schüttgut";
  }

  if (DEPONIE_RECYCLING_GROUPS.has(normalizedProductGroup)) {
    return "Deponie/Recycling";
  }

  return "Sonstiges";
};

const createEmptyValues = (): Record<AmountCategory, AmountValues> => ({
  Mischgut: {
    offered: 0,
    ordered: 0,
    open: 0,
  },
  Schüttgut: {
    offered: 0,
    ordered: 0,
    open: 0,
  },
  "Deponie/Recycling": {
    offered: 0,
    ordered: 0,
    open: 0,
  },
  Sonstiges: {
    offered: 0,
    ordered: 0,
    open: 0,
  },
});

const createEmptyTreeRow = (path: string, level: TreeLevel, label: string, number?: string, name?: string): InternalAmountTreeRow => ({
  id: path,
  path,
  level,
  label,
  number,
  name,
  values: createEmptyValues(),
  units: {
    Mischgut: "t",
    Schüttgut: "t",
    "Deponie/Recycling": "t",
    Sonstiges: "t",
  },
  unitSets: {
    Mischgut: new Set<string>(),
    Schüttgut: new Set<string>(),
    "Deponie/Recycling": new Set<string>(),
    Sonstiges: new Set<string>(),
  },
});

const getProjectLabel = (row: AmountListRow) => {
  return `${row.project_number ?? ""} ${row.project_name ?? ""}`.trim() || "Ohne Projekt";
};

const getOrderLabel = (row: AmountListRow) => {
  const validFrom = row.valid_from ? dayjs(row.valid_from, "YYYY-MM-DD").format("DD.MM.YYYY") : "";
  const validUntil = row.valid_until ? dayjs(row.valid_until, "YYYY-MM-DD").format("DD.MM.YYYY") : "";

  const validity = [validFrom, validUntil].filter(Boolean).join(" - ");

  if (validity) {
    return `${row.order_number || "Ohne Auftrag"} (${validity})`;
  }

  return row.order_number || "Ohne Auftrag";
};

const getSubGroupLabel = (row: AmountListRow) => {
  return row.product_sub_group || "Ohne Produktuntergruppe";
};

const getMaterialLabel = (row: AmountListRow) => {
  return `${row.material_number ?? ""} ${row.material_name ?? ""}`.trim() || "Ohne Artikel";
};

const addAmountToLayer = (row: InternalAmountTreeRow, category: AmountCategory, values: AmountValues, unit: string) => {
  row.values[category].offered += values.offered;
  row.values[category].ordered += values.ordered;
  row.unitSets[category].add(unit);
};

function buildAmountTreeRows(rows: AmountListRow[]): AmountTreeRow[] {
  const out: Record<string, InternalAmountTreeRow> = {};

  for (const row of rows) {
    const values = getAmountValues(row);

    if (!values.offered && !values.ordered) {
      continue;
    }

    const category = getAmountCategory(row.product_group);
    const unit = normalizeUnit(row.unit);

    const customerLabel = row.customer_name || "Ohne Kunde";
    const projectLabel = getProjectLabel(row);
    const orderLabel = getOrderLabel(row);
    const subGroupLabel = getSubGroupLabel(row);
    const materialLabel = getMaterialLabel(row);

    const layer1 = customerLabel;
    const layer2 = [customerLabel, projectLabel].join(TREE_SEPARATOR);
    const layer3 = [customerLabel, projectLabel, orderLabel].join(TREE_SEPARATOR);
    const layer4 = [customerLabel, projectLabel, orderLabel, subGroupLabel].join(TREE_SEPARATOR);
    const layer5 = [customerLabel, projectLabel, orderLabel, subGroupLabel, materialLabel].join(TREE_SEPARATOR);

    if (!(layer1 in out)) {
      out[layer1] = createEmptyTreeRow(layer1, "customer", customerLabel);
    }

    if (!(layer2 in out)) {
      out[layer2] = createEmptyTreeRow(layer2, "project", projectLabel, row.project_number, row.project_name);
    }

    if (!(layer3 in out)) {
      out[layer3] = createEmptyTreeRow(layer3, "order", orderLabel);
    }

    if (!(layer4 in out)) {
      out[layer4] = createEmptyTreeRow(layer4, "subGroup", subGroupLabel);
    }

    if (!(layer5 in out)) {
      out[layer5] = createEmptyTreeRow(layer5, "material", materialLabel, row.material_number, row.material_name);
    }

    for (const layer of [layer1, layer2, layer3, layer4, layer5]) {
      addAmountToLayer(out[layer], category, values, unit);
    }
  }

  return Object.values(out)
    .map((row) => {
      const units = { ...row.units };
      const values = createEmptyValues();

      for (const category of AMOUNT_CATEGORIES) {
        units[category] = getResolvedUnit(row.unitSets[category]);

        values[category] = {
          offered: row.values[category].offered,
          ordered: row.values[category].ordered,
          open: Math.max(row.values[category].offered - row.values[category].ordered, 0),
        };
      }

      return {
        id: row.id,
        path: row.path,
        level: row.level,
        label: row.label,
        number: row.number,
        name: row.name,
        values,
        units,
      };
    })
    .sort((a, b) => a.path.localeCompare(b.path));
}

function AmountTreeGroupingCell(params: GridRenderCellParams<AmountTreeRow>) {
  const apiRef = useGridApiContext();
  const rowNode = params.rowNode;
  const depth = getTreeDataPath(params.row).length - 1;

  const hasChildren = "children" in rowNode && Array.isArray(rowNode.children) && rowNode.children.length > 0;
  const expanded = "childrenExpanded" in rowNode && !!rowNode.childrenExpanded;

  const toggleExpanded = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (hasChildren) {
      apiRef.current.setRowChildrenExpansion(params.id, !expanded);
    }
  };

  const isProject = params.row.level === "project";
  const isMaterial = params.row.level === "material";

  return (
    <Box
      sx={{
        pl: depth * 2,
        display: "flex",
        alignItems: "center",
        minWidth: 0,
        width: "100%",
        font: "inherit",
      }}
    >
      <Box sx={{ width: 28, display: "flex", justifyContent: "center", flexShrink: 0 }}>
        {hasChildren ? (
          <IconButton size="small" onClick={toggleExpanded} tabIndex={-1}>
            {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
          </IconButton>
        ) : null}
      </Box>

      {isProject || isMaterial ? (
        <Box
          component="span"
          sx={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            font: "inherit",
          }}
        >
          {params.row.number ? (
            <Box
              component="span"
              sx={{
                fontSize: "80%",
                opacity: 0.9,
                mr: 0.75,
              }}
            >
              {params.row.number}
            </Box>
          ) : null}

          <Box component="span" sx={{ font: "inherit" }}>
            {params.row.name}
          </Box>
        </Box>
      ) : (
        <Box
          component="span"
          sx={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            font: "inherit",
          }}
        >
          {params.row.label}
        </Box>
      )}
    </Box>
  );
}

function AmountTooltipContent({ values, unit }: { values: AmountValues; unit: string }) {
  return (
    <Box sx={{ fontSize: 12, lineHeight: 1.6 }}>
      <Box>Auftragsmenge: {formatAmount(values.offered, unit) || `0 ${unit}`}</Box>
      <Box>Abgerufen: {formatAmount(values.ordered, unit) || `0 ${unit}`}</Box>
      <Box>Offen: {formatAmount(values.open, unit) || `0 ${unit}`}</Box>
    </Box>
  );
}

function getAmountListColumns(amountMode: AmountMode): GridColDef<AmountTreeRow>[] {
  return AMOUNT_CATEGORIES.map((category) => ({
    field: category,
    headerName: category,
    width: category === "Deponie/Recycling" ? 170 : 140,
    type: "number",
    align: "right",
    headerAlign: "right",
    valueGetter: (_, row) => row.values[category][amountMode] ?? 0,
    valueFormatter: (_, row) => formatAmount(row.values[category][amountMode], row.units[category] ?? "t"),
    renderCell: (params) => {
      const values = params.row.values[category];
      const unit = params.row.units[category] ?? "t";
      const displayValue = formatAmount(values[amountMode], unit);

      if (!displayValue) {
        return "";
      }

      return (
        <Tooltip title={<AmountTooltipContent values={values} unit={unit} />} arrow placement="top">
          <Box
            component="span"
            sx={{
              width: "100%",
              textAlign: "right",
              font: "inherit",
              cursor: "default",
            }}
          >
            {displayValue}
          </Box>
        </Tooltip>
      );
    },
  }));
}

export default function AmountList({ allowedPlant }: { allowedPlant: number }) {
  const [open, setOpen] = useState(false);
  const [plant, setPlant] = useState(Math.max(allowedPlant, 0));
  const [amountMode, setAmountMode] = useState<AmountMode>("open");

  const apiRef = useGridApiRef();

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const materialQuery = useQuery<AmountListRow[], AxiosResponse>({
    queryKey: ["getAmountList", "" + plant],
    queryFn: ApiService.getAmountList(plant),
    enabled: plant > 0,
  });

  const sourceRows = materialQuery.data ?? [];

  const rows = useMemo(() => buildAmountTreeRows(sourceRows), [sourceRows]);

  const columns = useMemo(() => getAmountListColumns(amountMode), [amountMode]);

  const { gridProps } = usePersistentDataGrid("amountlist", columns, {
    pinnedColumns: {
      right: AMOUNT_CATEGORIES,
    },
  });

  return (
    <Fragment>
      <Button startIcon={<Summarize />} variant="outlined" size="small" onClick={() => setOpen(true)}>
        Mengenliste
      </Button>

      {open ? (
        <Dialog open={open} maxWidth="xl" fullWidth onClose={close}>
          <DialogTitle
            sx={{
              p: 1,
              px: 2,
              background: "#002196",
              color: "white",
              borderBottom: "1px solid rgba(255,255,255,0.5)",
            }}
          >
            Mengenliste
          </DialogTitle>

          <DialogContent
            sx={{
              p: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              height: "75vh",
            }}
          >
            <Box
              sx={{
                p: 1,
                borderBottom: "1px solid #aaa",
                display: "flex",
                gap: 1,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {allowedPlant < 0 ? (
                <Box sx={{ minWidth: 260 }}>
                  <ManyToOneSelect version="app" entity="plant" onChange={(x) => setPlant(x.id)} valueId={plant} />
                </Box>
              ) : (
                <Box />
              )}

              <ToggleButtonGroup
                size="small"
                exclusive
                value={amountMode}
                onChange={(_, value: AmountMode | null) => {
                  if (value) {
                    setAmountMode(value);
                  }
                }}
              >
                <ToggleButton value="offered">Auftragsmenge</ToggleButton>
                <ToggleButton value="ordered">Abgerufen</ToggleButton>
                <ToggleButton value="open">Offen</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0 }}>
              <StyledDataGrid<AmountTreeRow>
                rows={rows}
                columns={columns}
                apiRef={apiRef}
                showToolbar
                rowHeight={32}
                loading={materialQuery.isLoading}
                treeData
                getTreeDataPath={getTreeDataPath}
                groupingColDef={{
                  headerName: "Kunde / Projekt / Auftrag / Untergruppe / Artikel",
                  hideDescendantCount: true,
                  flex: 1,
                  minWidth: 480,
                  renderCell: (params) => <AmountTreeGroupingCell {...params} />,
                }}
                disableRowSelectionOnClick
                getRowClassName={(params: GridRowClassNameParams<AmountTreeRow>) => "amountlist_level" + getTreeDataPath(params.row).length}
                {...gridProps}
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ borderTop: "1px solid #aaa" }}>
            <Button onClick={close}>Schließen</Button>
          </DialogActions>
        </Dialog>
      ) : null}
    </Fragment>
  );
}
